/**
 * TX-Statewide Business Regions (Feb 2026 CEO expansion directive)
 * ────────────────────────────────────────────────────────────────
 * The file name is preserved for backwards compatibility but the
 * catalog now spans the 5 launch metros: Dallas · Houston · Austin ·
 * San Antonio · Tyler. Each region carries a stable region_code +
 * `big_city` so the shared frontend forms (AddEmployeeDialog, Vehicle
 * form, Access Request, Profile page) can cascade State → Big City →
 * Region → City exactly like the backend seeded catalog.
 *
 * The DFW_REGIONS export is preserved as an alias to
 * TX_REGIONS so no downstream import breaks.
 */

// ── Dallas (DFW) metro — R01…R15 ─────────────────────────────────
const DALLAS_REGIONS = [
  { code: 'R01', name: 'North Dallas',                 cities: ['Frisco','Plano','McKinney','Allen','Prosper','Celina','Richardson','Addison','Carrollton','Farmers Branch','The Colony','Little Elm','Princeton','Murphy','Parker','Lucas','Fairview'] },
  { code: 'R02', name: 'Lewisville Corridor',          cities: ['Lewisville','Flower Mound','Highland Village','Double Oak','Copper Canyon','Lake Dallas','Corinth','Hickory Creek','Argyle','Bartonville','Lantana'] },
  { code: 'R03', name: 'Denton County North',          cities: ['Denton','Aubrey','Sanger','Krum','Ponder','Pilot Point','Justin','Roanoke','Trophy Club','Northlake'] },
  { code: 'R04', name: 'Las Colinas / Irving Corridor', cities: ['Irving','Las Colinas','Valley Ranch','Coppell'] },
  { code: 'R05', name: 'Grapevine Airport Corridor',   cities: ['Grapevine','Southlake','Colleyville','Euless','Bedford','Hurst','Keller'] },
  { code: 'R06', name: 'Arlington / Grand Prairie',    cities: ['Arlington','Grand Prairie','Mansfield','Dalworthington Gardens','Pantego'] },
  { code: 'R07', name: 'Central Dallas',               cities: ['Downtown Dallas','Uptown','Oak Lawn','Deep Ellum','Design District','Medical District','Love Field','University Park','Highland Park'] },
  { code: 'R08', name: 'East Dallas',                  cities: ['Mesquite','Garland','Rowlett','Rockwall','Sachse','Wylie','Sunnyvale','Forney'] },
  { code: 'R09', name: 'South Dallas',                 cities: ['Duncanville','Cedar Hill','DeSoto','Lancaster','Hutchins','Glenn Heights','Red Oak'] },
  { code: 'R10', name: 'Fort Worth North',             cities: ['Fort Worth','Alliance','Haslet','Saginaw','Blue Mound','Lake Worth'] },
  { code: 'R11', name: 'Mid-Cities',                   cities: ['Richland Hills','North Richland Hills','Watauga','Haltom City','North Fort Worth','White Settlement'] },
  { code: 'R12', name: 'West Fort Worth',              cities: ['Benbrook','Aledo','Willow Park','Hudson Oaks','Weatherford'] },
  { code: 'R13', name: 'South Fort Worth',             cities: ['Burleson','Crowley','Everman','Forest Hill','Kennedale'] },
  { code: 'R14', name: 'Ellis County Expansion',       cities: ['Waxahachie','Midlothian','Ennis','Ferris'] },
  { code: 'R15', name: 'Kaufman County Expansion',     cities: ['Terrell','Kaufman','Crandall'] },
].map((r) => ({ ...r, big_city: 'Dallas', state: 'TX' }));

// ── Houston metro — H1…H4 (matches backend TX_HIERARCHY seed) ────
const HOUSTON_REGIONS = [
  { code: 'H1', name: 'Inner Loop High-Density Core',    cities: ['Downtown Houston','Midtown','Medical Center','Montrose'] },
  { code: 'H2', name: 'West Luxury & Corporate Belt',    cities: ['Katy','Sugar Land','Westchase','Energy Corridor'] },
  { code: 'H3', name: 'North Growth Megabelt',           cities: ['Cypress','Spring','The Woodlands'] },
  { code: 'H4', name: 'South / Industrial Housing Belt', cities: ['Pearland','Pasadena','League City'] },
].map((r) => ({ ...r, big_city: 'Houston', state: 'TX' }));

// ── Austin metro — CT1…CT3 ──────────────────────────────────────
const AUSTIN_REGIONS = [
  { code: 'CT1', name: 'Austin Core Urban Loop',   cities: ['Downtown Austin','East Austin','South Austin'] },
  { code: 'CT2', name: 'Tech Corridor North',      cities: ['Round Rock','Pflugerville','Cedar Park'] },
  { code: 'CT3', name: 'Student Housing South',    cities: ['San Marcos','Kyle','Buda'] },
].map((r) => ({ ...r, big_city: 'Austin', state: 'TX' }));

// ── San Antonio metro — ST1…ST3 ─────────────────────────────────
const SAN_ANTONIO_REGIONS = [
  { code: 'ST1', name: 'Central San Antonio Core',            cities: ['Downtown San Antonio','Medical Center District'] },
  { code: 'ST2', name: 'Military & Stable Housing Corridor',  cities: ['Schertz','Universal City','Converse','Live Oak'] },
  { code: 'ST3', name: 'West Expansion Luxury Belt',          cities: ['Helotes','Shavano Park','Leon Valley'] },
].map((r) => ({ ...r, big_city: 'San Antonio', state: 'TX' }));

// ── Tyler / East Texas metro — ET1…ET2 ──────────────────────────
const TYLER_REGIONS = [
  { code: 'ET1', name: 'Tyler Core',                cities: ['South Tyler','Downtown Tyler'] },
  { code: 'ET2', name: 'East Texas Satellite Loop', cities: ['Longview','Kilgore','Jacksonville'] },
].map((r) => ({ ...r, big_city: 'Tyler', state: 'TX' }));

// Union of all TX regions. Order matters — Dallas first (launch
// metro), followed by the expansion metros south-to-north-east.
export const TX_REGIONS = [
  ...DALLAS_REGIONS,
  ...HOUSTON_REGIONS,
  ...AUSTIN_REGIONS,
  ...SAN_ANTONIO_REGIONS,
  ...TYLER_REGIONS,
];

// Backwards-compat alias — several files still import DFW_REGIONS.
export const DFW_REGIONS = TX_REGIONS;

/** Regions for a specific big city — cascades the Big City filter. */
export function getRegionsForBigCity(bigCity) {
  if (!bigCity) return [];
  return TX_REGIONS.filter((r) => r.big_city === bigCity);
}

/** Look up a region by its short code. */
export function getRegionByCode(code) {
  if (!code) return null;
  return TX_REGIONS.find((r) => r.code === code) || null;
}

/** Look up a region by its display name (case-insensitive). */
export function getRegionByName(name) {
  if (!name) return null;
  const target = String(name).trim().toLowerCase();
  return TX_REGIONS.find((r) => r.name.toLowerCase() === target) || null;
}

/** Big-city for a given region code — null if unknown. */
export function getBigCityForRegionCode(code) {
  const r = getRegionByCode(code);
  return r ? r.big_city : null;
}

/** Friendly region label — accepts code OR name. */
export function resolveRegionLabel(value) {
  if (!value) return null;
  const byCode = getRegionByCode(value);
  if (byCode) return `${byCode.code} · ${byCode.name}`;
  const byName = getRegionByName(value);
  if (byName) return `${byName.code} · ${byName.name}`;
  return String(value);
}

/** All region {code, name} pairs — handy for dropdown <option> rendering. */
export const REGION_OPTIONS = TX_REGIONS.map((r) => ({ code: r.code, name: r.name }));
